import { useCallback } from 'react';
import { getCastService, getDramaService } from '../service/ServiceRegistry';
import type { FetchDataFn, TableQueryParams, TableDataResponse } from '../types/table';
import type {
  Cast,
  CastCreateRequest,
  CastUpdateRequest,
  CastsPage,
} from '../types/api/cast';
import type { ContentCastResponse, DramaCast } from '../types/api/drama';

/**
 * Hook for cast listing (DataTable integration).
 */
export function useCasts() {
  const castService = getCastService();

  const fetchCasts: FetchDataFn<Cast> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<Cast>> => {
      try {
        const apiParams = {
          page: params.page, // backend now 1-based
          size: params.pageSize,
          keyword: params.search,
        };

        const response: CastsPage = await castService.getCasts(apiParams);

        return {
          data: response.items,
          pagination: {
            page: response.page,
            pageSize: response.size,
            total: response.totalItems,
            totalPages: response.totalPages,
            hasNext: response.hasNext,
            hasPrev: response.hasPrevious,
          },
        };
      } catch (error) {
        // Let caller handle error (DataTable or page)
        console.error('Failed to fetch casts:', error);
        throw new Error('Failed to load casts. Please try again.');
      }
    },
    [castService]
  );

  return {
    fetchCasts,
  };
}

/**
 * Hook for cast CRUD and lookup actions.
 */
export function useCastActions() {
  const castService = getCastService();

  const createCastWithJson = useCallback(
    async (payload: CastCreateRequest): Promise<Cast> => {
      try {
        return await castService.createCastWithJson(payload);
      } catch (error) {
        console.error('Failed to create cast (JSON):', error);
        throw error;
      }
    },
    [castService]
  );

  const createCastWithImage = useCallback(
    async (payload: CastCreateRequest, profileImageFile?: File): Promise<Cast> => {
      try {
        return await castService.createCastWithImage(payload, profileImageFile);
      } catch (error) {
        console.error('Failed to create cast with image:', error);
        throw error;
      }
    },
    [castService]
  );

  const updateCastWithJson = useCallback(
    async (castId: string, payload: CastUpdateRequest): Promise<Cast> => {
      try {
        return await castService.updateCastWithJson(castId, payload);
      } catch (error) {
        console.error('Failed to update cast (JSON):', error);
        throw error;
      }
    },
    [castService]
  );

  const updateCastWithImage = useCallback(
    async (
      castId: string,
      payload: CastUpdateRequest,
      profileImageFile?: File
    ): Promise<Cast> => {
      try {
        return await castService.updateCastWithImage(castId, payload, profileImageFile);
      } catch (error) {
        console.error('Failed to update cast with image:', error);
        throw error;
      }
    },
    [castService]
  );

  const deleteCast = useCallback(
    async (castId: string): Promise<void> => {
      try {
        await castService.deleteCast(castId);
      } catch (error) {
        console.error('Failed to delete cast:', error);
        throw error;
      }
    },
    [castService]
  );

  const getCast = useCallback(
    async (castId: string): Promise<Cast> => {
      try {
        return await castService.getCast(castId);
      } catch (error) {
        console.error('Failed to fetch cast:', error);
        throw error;
      }
    },
    [castService]
  );

  const searchCasts = useCallback(
    async (keyword: string, page?: number, size?: number): Promise<CastsPage> => {
      try {
        return await castService.searchCasts(keyword, { page, size });
      } catch (error) {
        console.error('Failed to search casts:', error);
        throw error;
      }
    },
    [castService]
  );

  const getCastsByNationality = useCallback(
    async (nationality: string): Promise<Cast[]> => {
      try {
        return await castService.getCastsByNationality(nationality);
      } catch (error) {
        console.error('Failed to fetch casts by nationality:', error);
        throw error;
      }
    },
    [castService]
  );

  const getPopularCasts = useCallback(
    async (minContentCount?: number): Promise<Cast[]> => {
      try {
        return await castService.getPopularCasts(minContentCount);
      } catch (error) {
        console.error('Failed to fetch popular casts:', error);
        throw error;
      }
    },
    [castService]
  );

  return {
    createCastWithJson,
    createCastWithImage,
    updateCastWithJson,
    updateCastWithImage,
    deleteCast,
    getCast,
    searchCasts,
    getCastsByNationality,
    getPopularCasts,
  };
}

/**
 * Hook for cast assignment to movies and dramas/series.
 *
 * - For movies: delegates to CastService movie assignment helpers.
 * - For dramas/series: delegates to DramaService cast helpers.
 */
export function useCastAssignment(contentType: 'movie' | 'drama' | 'music-video', contentId: string) {
  const castService = getCastService();
  const dramaService = getDramaService();

  const assignCast = useCallback(
    async (castId: string, role: string): Promise<ContentCastResponse> => {
      try {
        if (contentType === 'movie') {
          return await castService.assignCastToMovie(contentId, { castId, role });
        }

        if (contentType === 'music-video') {
          throw new Error('Cast assignment is not supported for music videos');
        }

        // Drama/Series
        return await dramaService.assignCast(contentId, { castId, role });
      } catch (error) {
        console.error('Failed to assign cast:', error);
        throw error;
      }
    },
    [castService, dramaService, contentType, contentId]
  );

  const removeCast = useCallback(
    async (castId: string): Promise<void> => {
      try {
        if (contentType === 'movie') {
          await castService.removeCastFromMovie(contentId, castId);
        } else if (contentType === 'music-video') {
          throw new Error('Cast removal is not supported for music videos');
        } else {
          await dramaService.removeCast(contentId, castId);
        }
      } catch (error) {
        console.error('Failed to remove cast:', error);
        throw error;
      }
    },
    [castService, dramaService, contentType, contentId]
  );

  const updateRole = useCallback(
    async (castId: string, role: string): Promise<ContentCastResponse> => {
      try {
        if (contentType === 'movie') {
          return await castService.updateMovieCastRole(contentId, castId, role);
        }

        if (contentType === 'music-video') {
          throw new Error('Cast role updates are not supported for music videos');
        }

        return await dramaService.updateCastRole(contentId, castId, role);
      } catch (error) {
        console.error('Failed to update cast role:', error);
        throw error;
      }
    },
    [castService, dramaService, contentType, contentId]
  );

  const getCasts = useCallback(async (): Promise<DramaCast[]> => {
    try {
      if (contentType === 'movie') {
        return await castService.getMovieCasts(contentId);
      }

      if (contentType === 'music-video') {
        throw new Error('Cast listing is not supported for music videos');
      }

      return await dramaService.getCasts(contentId);
    } catch (error) {
      console.error('Failed to fetch casts for content:', error);
      throw error;
    }
  }, [castService, dramaService, contentType, contentId]);

  return {
    assignCast,
    removeCast,
    updateRole,
    getCasts,
  };
}


